import {
  Box,
  Button,
  Link as ButtonLink,
  Checkbox,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Typography,
} from '@material-ui/core';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import * as Sentry from '@sentry/react';
import { differenceInDays, format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';

import { showErrorAction } from 'containers/Notification/actions';
import { selectUser } from 'entities/user/selectors';
import {
  EligibilityFormStatus,
  SortOrder,
  VolunteerReward,
  VolunteerRewardApprovalStatus,
  VolunteerRewardsDocument,
  VolunteerRewardsQueryVariables,
  useUpdateVolunteerRewardMutation,
  useVolunteerRewardsQuery,
} from 'generated/graphql';
import PageHeader from 'retired/PageHeader';
import { TableBox } from 'retired/TableBox';
import TablePaginationActions from 'retired/TablePaginationActions';
import { orange, primaryBlue, red } from 'utils/color-styles';
import { convertDuration } from 'utils/convertDuration';
import { CurrencyCode, currencyFormat } from 'utils/currencyFormat';
import { Features } from 'utils/features';
import { formatFullName } from 'utils/formatFullName';
import { Privileges } from 'utils/privileges';
import { truthy } from 'utils/truthy';
import { truncateString } from 'utils/truncateString';

import { RejectionWarningModal } from './RejectionWarningModal';
import { Tabs } from './Tabs';
import { VolunteerRewardDetailModal } from './VolunteerRewardDetailModal';
import { DaysDiffInfoText } from './DaysDifferenceInfoText';
import Filters, { FilterStatus } from './Filters';

const MAX_DAYS_FOR_APPROVAL = 90;

enum TableSortValues {
  formSubmittedAt = 'formSubmittedAt',
  volunteerTimeDate = 'volunteerTimeDate',
  volunteerTimeApprovedAt = 'volunteerTimeApprovedAt',
  employeeHireDate = 'employeeHireDate',
  amount = 'amount',
  eventName = 'eventName',
  userName = 'userName',
}

function getApplicationTextColor(volunteerReward: VolunteerReward) {
  if (volunteerReward.formSubmittedAt && volunteerReward.VolunteerTime?.approvedAt) {
    const diff = differenceInDays(
      new Date(volunteerReward.formSubmittedAt),
      new Date(volunteerReward.VolunteerTime.approvedAt)
    );
    if (diff < -MAX_DAYS_FOR_APPROVAL) {
      return red;
    } else if (diff === -MAX_DAYS_FOR_APPROVAL) {
      return orange;
    }
  }
  return undefined;
}

const styles = {
  nameLink: {
    width: 110,
    display: 'block',
    lineHeight: '32px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  link: {
    display: 'inline-block',
    lineHeight: '22px',
  },
  tableHeader: {
    justifyContent: 'flex-start',
  },
  tableHeaderControls: {
    marginLeft: 'auto',
  },
  tableHeaderControl: {
    marginLeft: '16px',
  },
  tableHeaderControlIcon: { marginLeft: 5, alignSelf: 'flex-start' },
};

const rowsPerPageOptions = [50, 100, 200];

const defaultSortOrder = SortOrder.Asc;
const defaultSortColumn = 'formSubmittedAt';

const defaultQueryVariables: VolunteerRewardsQueryVariables = {
  where: {
    approvalStatus: {
      equals: VolunteerRewardApprovalStatus.Pending,
    },
    formStatus: {
      in: [EligibilityFormStatus.NotRequired, EligibilityFormStatus.Eligible],
    },
  },
  orderByList: [
    {
      [defaultSortColumn]: defaultSortOrder,
    },
    {
      createdAt: defaultSortOrder,
    },
  ],
  skip: 0,
  take: rowsPerPageOptions[0],
};

const VolunteerRewardsListPage: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [volunteerRewardDetails, setVolunteerRewardDetails] = useState<VolunteerReward>();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectionWarningModal, setShowRejectionWarningModal] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(new Map<string, boolean>());

  const user = useSelector(selectUser);
  if (user && !user.hasPrivilege(Privileges.VOLUNTEER_REWARDS_MANAGE)) {
    history.replace('/');
  }

  const [selectedFilterStatus, setSelectedFilterStatus] = useState<FilterStatus>(
    VolunteerRewardApprovalStatus.Pending
  );
  const [searchValue, setSearchValue] = useState('');
  const [queryVariables, setQueryVariables] =
    useState<VolunteerRewardsQueryVariables>(defaultQueryVariables);

  const {
    data,
    error: volunteerRewardError,
    loading: volunteerRewardLoading,
  } = useVolunteerRewardsQuery({
    variables: queryVariables,
    // We unfortunately need to disable caching, because it is tricky when users approve
    // different rewards on different pages. In this scenario we need to refetch those multiple specific pages.
    // Instead we decided to go for the bulletproof option, to always refetch and start from page 1
    fetchPolicy: 'network-only',
    skip: !queryVariables,
  });

  const [
    updateVolunteerRewards,
    { loading: updateVolunteerRewardLoading, error: updateVolunteerRewardError },
  ] = useUpdateVolunteerRewardMutation({
    onCompleted: () => {
      setQueryVariables({
        ...queryVariables,
        skip: 0,
      });

      setStatusUpdating(new Map());
      setShowRejectionWarningModal(false);
      setSelectedItems([]);
    },
    onError: () => {
      setStatusUpdating(new Map());
      setShowRejectionWarningModal(false);
      dispatch(showErrorAction('Failed to update Volunteer Grants'));
      Sentry.captureException(updateVolunteerRewardError, { level: 'error' });
    },
    // We need this only for the first page, because in other cases setPage(0); triggers a rerender
    // which results in a refetch
    ...(queryVariables?.skip === 0
      ? {
          refetchQueries: [
            {
              query: VolunteerRewardsDocument,
              variables: queryVariables,
            },
          ],
        }
      : {}),
  });

  const anyStatusUpdating =
    Boolean(Object.values(Object.fromEntries(statusUpdating)).filter(Boolean).length) ||
    volunteerRewardLoading ||
    updateVolunteerRewardLoading;

  const volunteerRewards = data?.volunteerRewards?.items;
  const volunteerRewardsMetaData = data?.volunteerRewards?.metadata;

  const handleCheckboxChange = (id: string) => {
    setSelectedItems((prevSelectedItems) => {
      if (prevSelectedItems.includes(id)) {
        return prevSelectedItems.filter((itemId) => itemId !== id);
      } else {
        return [...prevSelectedItems, id];
      }
    });
  };

  const handleSelectAll = () => {
    const selectedItemsForCurrentPage = selectedItems.filter((item) =>
      volunteerRewards?.some((vr) => vr.id === item)
    );
    const isAllSelectedForCurrentPage =
      selectedItemsForCurrentPage.length === volunteerRewards?.length;

    if (isAllSelectedForCurrentPage) {
      // if all selected for current page then deselect all from the current page
      setSelectedItems(selectedItems.filter((item) => !selectedItemsForCurrentPage.includes(item)));
    } else {
      // if not selected all then select the rest of all for current page
      const notSelectedCurrentPageItems = (volunteerRewards || [])
        .map((item) => (!selectedItemsForCurrentPage.includes(item.id) ? item.id : null))
        .filter(truthy);

      setSelectedItems((prevSelectedItems) => [
        ...prevSelectedItems,
        ...notSelectedCurrentPageItems,
      ]);
    }
  };

  const handleSubmit = (
    approvalStatus: VolunteerRewardApprovalStatus.Approved | VolunteerRewardApprovalStatus.Rejected,
    ids?: string[]
  ) => {
    const selectedIds = ids || selectedItems;

    if (!selectedIds?.length || anyStatusUpdating) {
      return;
    }

    setStatusUpdating(selectedIds.reduce((acc, id) => acc.set(id, true), new Map()));

    updateVolunteerRewards({
      variables: {
        ids: selectedIds,
        data: {
          approvalStatus,
        },
      },
    });
  };

  const searchParams = new URLSearchParams(location.search);

  // The useEffect below will react to the url changes and make the requests accordingly
  const addSortParametersToUrl = (column: string): void => {
    const previousOrder = searchParams.get('order') || defaultSortOrder;
    const order = previousOrder === 'asc' ? 'desc' : 'asc';

    searchParams.set('column', column);
    searchParams.set('order', order);

    history.replace({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`,
    });
  };

  // Handles all the sorting logic
  useEffect(() => {
    const order = searchParams.get('order');
    const column = searchParams.get('column');

    // Initial render if no search params are in the url
    if (!queryVariables || !order || !column) {
      return;
    }

    let nestedColumnQuery;
    switch (column) {
      case TableSortValues.userName:
        nestedColumnQuery = { User: { fullName: { first: order } } };
        break;

      case TableSortValues.volunteerTimeDate:
        nestedColumnQuery = { VolunteerTime: { date: order } };
        break;

      case TableSortValues.volunteerTimeApprovedAt:
        nestedColumnQuery = { VolunteerTime: { approvedAt: order } };
        break;

      case TableSortValues.employeeHireDate:
        nestedColumnQuery = {
          User: {
            additionalData: { hireDate: order },
          },
        };
        break;

      case TableSortValues.eventName:
        nestedColumnQuery = {
          VolunteerTime: {
            Deed: { name: order },
          },
        };
        break;

      default:
        break;
    }

    const newQueryVariables = {
      ...queryVariables,
      orderByList: [
        nestedColumnQuery || {
          [column]: order,
        },
        {
          createdAt: order,
        },
      ],
      skip: defaultQueryVariables.skip,
      take: defaultQueryVariables.take,
    };

    if (
      JSON.stringify(newQueryVariables?.orderByList) === JSON.stringify(queryVariables?.orderByList)
    ) {
      return;
    }

    setQueryVariables(newQueryVariables as VolunteerRewardsQueryVariables);
    setSelectedItems([]);
  }, [searchParams]);

  const rowsPerPage = queryVariables?.take ?? defaultQueryVariables.take;
  const page = queryVariables?.skip ? queryVariables.skip / rowsPerPage : 0;
  const order = searchParams.get('order') || defaultSortOrder;

  const isPendingFilter =
    queryVariables?.where?.approvalStatus?.equals === VolunteerRewardApprovalStatus.Pending;

  const handleOpenModal = (volunteerReward: VolunteerReward) => {
    setVolunteerRewardDetails(volunteerReward);
    setShowDetailsModal(true);
    // Add the ID to URL without affecting other params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('rewardId', volunteerReward.id);
    history.replace({
      pathname: location.pathname,
      search: `?${newSearchParams.toString()}`,
    });
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setVolunteerRewardDetails(undefined);
    // Remove the ID from URL without affecting other params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('rewardId');
    history.replace({
      pathname: location.pathname,
      search: `?${newSearchParams.toString()}`,
    });
  };

  // Check URL for rewardId on mount and open modal if found
  useEffect(() => {
    const rewardId = searchParams.get('rewardId');
    if (rewardId && volunteerRewards) {
      const reward = volunteerRewards.find((r) => r.id === rewardId);
      if (reward) {
        setVolunteerRewardDetails(reward);
        setShowDetailsModal(true);
      }
    }
  }, [volunteerRewards]);

  return (
    <>
      <Helmet title="Volunteer Grants" />
      <PageHeader title="Volunteer Grants" onArrowClick={() => history.push('/volunteers')} />
      {user?.hasFeature(Features.VOLUNTEER_REWARD) ? <Tabs selectedTab="VolunteerReward" /> : null}
      <Filters
        setQueryVariables={setQueryVariables}
        defaultQueryVariables={defaultQueryVariables}
        searchValue={searchValue}
        selectedFilterStatus={selectedFilterStatus}
        setSelectedFilterStatus={setSelectedFilterStatus}
      />
      <TableBox
        fixedBottom={
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            colSpan={6}
            count={volunteerRewardsMetaData?.totalItems || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_event, newPage): void => {
              if (!queryVariables) {
                return;
              }
              setQueryVariables({
                ...queryVariables,
                skip: newPage * queryVariables.take,
              });
            }}
            onChangePage={(_event, newPage): void => {
              if (!queryVariables) {
                return;
              }
              setQueryVariables({
                ...queryVariables,
                skip: newPage * queryVariables.take,
              });
            }}
            onRowsPerPageChange={(event): void => {
              if (!queryVariables) {
                return;
              }
              setQueryVariables({
                ...queryVariables,
                take: parseInt(event.target.value, 10),
                skip: 0,
              });
            }}
            ActionsComponent={TablePaginationActions}
          />
        }
      >
        <Toolbar style={styles.tableHeader}>
          <Typography
            variant="h6"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Employee Submissions
          </Typography>
          <Box style={styles.tableHeaderControls}>
            <TextField
              type="search"
              placeholder="Search by employee name, email"
              style={{ width: '280px' }}
              variant="standard"
              InputProps={{
                startAdornment: <SearchIcon />,
              }}
              onChange={(e) => setSearchValue(e.target.value)}
              value={searchValue}
            />
            {isPendingFilter && (
              <>
                <Button
                  style={{
                    ...styles.tableHeaderControl,
                    ...(!(selectedItems.length < 1 || anyStatusUpdating) && {
                      color: red,
                      border: `1px solid ${red}`,
                    }),
                  }}
                  disabled={selectedItems.length < 1 || anyStatusUpdating}
                  onClick={() => setShowRejectionWarningModal(true)}
                >
                  {selectedItems.length === 1 ? (
                    <>Reject {selectedItems.length} submission</>
                  ) : (
                    <>Reject {selectedItems.length} submissions</>
                  )}
                  <BlockIcon style={styles.tableHeaderControlIcon} />
                </Button>
                <Button
                  style={styles.tableHeaderControl}
                  disabled={selectedItems.length < 1 || anyStatusUpdating}
                  onClick={() => handleSubmit(VolunteerRewardApprovalStatus.Approved)}
                >
                  {selectedItems.length === 1 ? (
                    <>Approve {selectedItems.length} submission</>
                  ) : (
                    <>Approve {selectedItems.length} submissions</>
                  )}
                  <CheckIcon style={styles.tableHeaderControlIcon} />
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
        <Table size="small">
          {!!volunteerRewards?.length && (
            <TableHead>
              <TableRow>
                {isPendingFilter && (
                  <TableCell size="medium">
                    <Checkbox
                      color="primary"
                      checked={volunteerRewards?.every((volunteerReward) =>
                        selectedItems.includes(volunteerReward.id)
                      )}
                      onChange={handleSelectAll}
                      disabled={anyStatusUpdating}
                    />
                  </TableCell>
                )}
                <TableCell size="medium">
                  <TableSortLabel
                    direction={order}
                    active={
                      (searchParams.get('column') || defaultSortColumn) === TableSortValues.userName
                    }
                    onClick={(): void => {
                      addSortParametersToUrl(TableSortValues.userName);
                    }}
                  >
                    Employee
                  </TableSortLabel>
                </TableCell>
                <TableCell size="medium">
                  <TableSortLabel
                    direction={order}
                    active={
                      (searchParams.get('column') || defaultSortColumn) ===
                      TableSortValues.eventName
                    }
                    onClick={(): void => {
                      addSortParametersToUrl(TableSortValues.eventName);
                    }}
                  >
                    Event / Organization
                  </TableSortLabel>
                </TableCell>
                <TableCell size="small">
                  <TableSortLabel
                    direction={order}
                    active={
                      (searchParams.get('column') || defaultSortColumn) === TableSortValues.amount
                    }
                    onClick={(): void => {
                      addSortParametersToUrl(TableSortValues.amount);
                    }}
                  >
                    Grant
                  </TableSortLabel>
                </TableCell>
                <TableCell size="small">
                  <TableSortLabel
                    direction={order}
                    active={
                      (searchParams.get('column') || defaultSortColumn) ===
                      TableSortValues.volunteerTimeDate
                    }
                    onClick={(): void => {
                      addSortParametersToUrl(TableSortValues.volunteerTimeDate);
                    }}
                  >
                    Volunteering Date
                  </TableSortLabel>
                </TableCell>
                <TableCell size="small">
                  <TableSortLabel
                    direction={order}
                    active={
                      (searchParams.get('column') || defaultSortColumn) ===
                      TableSortValues.employeeHireDate
                    }
                    onClick={(): void => {
                      addSortParametersToUrl(TableSortValues.employeeHireDate);
                    }}
                  >
                    Employee Hire Date
                  </TableSortLabel>
                </TableCell>
                <TableCell size="medium">
                  <TableSortLabel
                    direction={order}
                    active={
                      (searchParams.get('column') || defaultSortColumn) ===
                      TableSortValues.formSubmittedAt
                    }
                    onClick={(): void => {
                      addSortParametersToUrl(TableSortValues.formSubmittedAt);
                    }}
                  >
                    Employee Applied for Grant
                  </TableSortLabel>
                </TableCell>
                <TableCell size="small">
                  <TableSortLabel
                    direction={order}
                    active={
                      (searchParams.get('column') || defaultSortColumn) ===
                      TableSortValues.volunteerTimeApprovedAt
                    }
                    onClick={(): void => {
                      addSortParametersToUrl(TableSortValues.volunteerTimeApprovedAt);
                    }}
                  >
                    Volunteer Hours Approved
                  </TableSortLabel>
                </TableCell>
                <TableCell size="small">Details</TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {volunteerRewardLoading ? (
              <LinearProgress />
            ) : volunteerRewardError ? (
              <TableRow>
                <TableCell colSpan={10} style={{ textAlign: 'center' }}>
                  <Typography>There was an error loading Volunteer Grants list</Typography>
                </TableCell>
              </TableRow>
            ) : !volunteerRewards?.length ? (
              <TableRow>
                <TableCell colSpan={10} style={{ textAlign: 'center' }}>
                  <Typography>
                    {searchValue
                      ? `No Volunteer Grants found matching "${searchValue}"`
                      : isPendingFilter
                      ? 'No pending Volunteer Grants to review at this time'
                      : 'No Volunteer Grants found with the current filters'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              volunteerRewards.map((volunteerReward) => (
                <TableRow key={volunteerReward.id}>
                  {isPendingFilter && (
                    <TableCell>
                      {statusUpdating.get(volunteerReward.id) ? (
                        <CircularProgress size={20} style={{ margin: '6.5px 9px' }} />
                      ) : (
                        <Checkbox
                          color="primary"
                          checked={selectedItems.includes(volunteerReward.id)}
                          onChange={() => handleCheckboxChange(volunteerReward.id)}
                          disabled={anyStatusUpdating}
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <Link
                      to={`/volunteers/${volunteerReward.User?.id}`}
                      className="hoverable"
                      style={styles.nameLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {formatFullName(volunteerReward.User?.fullName)}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {volunteerReward.Deed?.name ? (
                      <Link
                        to={`/deeds/events/${volunteerReward.Deed.id}`}
                        className="hoverable"
                        style={styles.link}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {truncateString(volunteerReward.Deed?.name, 35)}
                      </Link>
                    ) : (
                      'External event'
                    )}
                    <div style={{ ...styles.link, margin: '0 3px' }}> for </div>
                    {/* In theory every volunteerReward has a volunteerTime.
                    It's only an additional check to prevent the whole page to crash. (We had an issue in the past where a the admins had access to the reward but not the vt which made the whole page crash ) */}
                    {volunteerReward.VolunteerTime?.Nonprofit && (
                      <Link
                        to={`/organizations/${volunteerReward.VolunteerTime.Nonprofit?.id}`}
                        className="hoverable"
                        style={styles.link}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {truncateString(volunteerReward.VolunteerTime.Nonprofit?.name || '', 35)}{' '}
                      </Link>
                    )}
                    {/* We don't use volunteerReward.nonprofit, because that's the np where the donation goes to. Admins are more interested of the np the user volunteered for */}
                  </TableCell>
                  <TableCell>
                    <div style={{ width: 'fit-content' }}>
                      {volunteerReward?.amount &&
                        currencyFormat(
                          volunteerReward.amount,
                          volunteerReward.currency as CurrencyCode
                        )}{' '}
                      for {convertDuration(volunteerReward.volunteerDuration)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {volunteerReward.VolunteerTime?.date &&
                      format(new Date(volunteerReward.VolunteerTime.date), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell
                    style={{
                      color:
                        new Date(volunteerReward.User.additionalData?.hireDate).getTime() >
                        new Date(volunteerReward.VolunteerTime?.date).getTime()
                          ? red
                          : undefined,
                    }}
                  >
                    {volunteerReward.User.additionalData?.hireDate &&
                      format(
                        new Date(volunteerReward.User.additionalData?.hireDate),
                        'MMM dd, yyyy'
                      )}
                  </TableCell>
                  <TableCell>
                    {volunteerReward.formSubmittedAt &&
                      format(new Date(volunteerReward.formSubmittedAt), 'MMM dd, yyyy')}
                    {volunteerReward.formSubmittedAt && volunteerReward.VolunteerTime?.date && (
                      <DaysDiffInfoText
                        date1={volunteerReward.VolunteerTime.date}
                        date2={volunteerReward.formSubmittedAt}
                        beforeOrAfter="volunteering"
                      />
                    )}
                  </TableCell>
                  <TableCell
                    style={{
                      color: volunteerReward && getApplicationTextColor(volunteerReward),
                    }}
                  >
                    {volunteerReward.VolunteerTime?.approvedAt
                      ? format(new Date(volunteerReward.VolunteerTime.approvedAt), 'MMM dd, yyyy')
                      : // Logging the timestamp was merged to prod on 11.06.24
                        'before Jun 11, 24'}
                    {volunteerReward.formSubmittedAt &&
                      volunteerReward.VolunteerTime?.approvedAt && (
                        <DaysDiffInfoText
                          date1={volunteerReward.formSubmittedAt}
                          date2={volunteerReward.VolunteerTime.approvedAt}
                          maxDiff={MAX_DAYS_FOR_APPROVAL}
                          beforeOrAfter="application"
                        />
                      )}
                  </TableCell>
                  <TableCell>
                    <ButtonLink
                      component="button"
                      style={{
                        color: primaryBlue,
                        cursor: 'pointer',
                        outline: 'none',
                      }}
                      onClick={() => handleOpenModal(volunteerReward)}
                    >
                      Details
                    </ButtonLink>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableBox>
      <VolunteerRewardDetailModal
        showModal={showDetailsModal}
        setShowModal={handleCloseModal}
        volunteerRewardDetails={volunteerRewardDetails}
        handleApproveVolunteerReward={(id) =>
          handleSubmit(VolunteerRewardApprovalStatus.Approved, [id])
        }
        handleRejectVolunteerReward={(id) =>
          handleSubmit(VolunteerRewardApprovalStatus.Rejected, [id])
        }
        filterStatus={selectedFilterStatus}
      />
      <RejectionWarningModal
        showModal={showRejectionWarningModal}
        setShowModal={setShowRejectionWarningModal}
        handleRejectVolunteerReward={() => handleSubmit(VolunteerRewardApprovalStatus.Rejected)}
      />
    </>
  );
};

export default VolunteerRewardsListPage;
